"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@repo/ui/button";
import { CloudinaryImage } from "@repo/ui/cloudinary-image";
import * as List from "@repo/ui/list";
import * as Sheet from "@repo/ui/sheet";
import { useIntersectionObserver } from "@repo/ui/useIntersectionObserver";
import { VideoPlayer } from "@repo/ui/video-player";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { Zoomable } from "@repo/ui/zoomable";
import { COLLAGE, STRINGS } from "@/constants";
import { ChevronLeft } from "@/theme/icons/chevron-left";
import { ChevronRight } from "@/theme/icons/chevron-right";
import { CloseIcon } from "@/theme/icons/close";
import classes from "./full-width-gallery.module.css";

type FullWidthGalleryProps = {
  productImagesWithVideo: string[];
  videoUrls: {
    mobile: string;
    desktop: string;
  };
  productName: string;
  onClose: () => void;
  defaultImageIndex: number;
};

export const FullWidthGallery = ({
  productImagesWithVideo,
  videoUrls,
  productName,
  onClose,
  defaultImageIndex,
}: FullWidthGalleryProps) => {
  const [galleryRef, setGalleryRef] = useState<HTMLUListElement>();
  const itemsRef = useRef<Array<HTMLLIElement>>([]);
  const [currentImage, setCurrentImage] = useState(defaultImageIndex);
  const [settled, setSettled] = useState(false);

  const galleryRefFunc = useCallback((node: HTMLUListElement | null) => {
    if (node !== null) {
      setGalleryRef(node);
    }
  }, []);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const intersectingIndex = itemsRef.current.indexOf(
          entry.target as HTMLLIElement,
        );

        if (intersectingIndex === defaultImageIndex && !settled) {
          setSettled(true);
        }

        if (entry.isIntersecting && settled) {
          setCurrentImage(intersectingIndex);
        }
      });
    },
    [settled, defaultImageIndex],
  );

  useIntersectionObserver({
    root: galleryRef,
    rootMargin: "0px",
    threshold: 1.0,
    containerReference: galleryRef,
    observableItems: itemsRef.current,
    callback: observerCallback,
  });

  useEffect(() => {
    if (galleryRef) {
      galleryRef.scrollTo({
        left:
          galleryRef.scrollWidth *
          (currentImage / productImagesWithVideo.length),
        behavior: settled ? "smooth" : "instant",
      });
    }
  }, [currentImage, productImagesWithVideo.length, galleryRef, settled]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keyup", handleKeyPress);

    return () => {
      window.removeEventListener("keyup", handleKeyPress);
    };
  }, [onClose]);

  const setCurrentImagePosition = (direction: string) => {
    if (direction === "next") {
      setCurrentImage((currentImage + 1) % productImagesWithVideo.length);
    } else {
      setCurrentImage(
        (currentImage - 1 + productImagesWithVideo.length) %
          productImagesWithVideo.length,
      );
    }
  };

  return (
    <Sheet.Root open={true}>
      <Sheet.Content
        position="center"
        overlayAnimation={true}
        className={classes.fullWidthGallery}
      >
        <VisuallyHidden>
          <Sheet.Header>
            <Sheet.Title>Product Gallery</Sheet.Title>
            <Sheet.Description>Zoomed in product gallery</Sheet.Description>
          </Sheet.Header>
        </VisuallyHidden>
        <Sheet.Main>
          <Sheet.Close
            onClick={onClose}
            className={classes.fullWidthGalleryClose}
            name="close"
            aria-label={STRINGS.gallery.ariaLabels.closeGallery}
          >
            <CloseIcon label={STRINGS.gallery.ariaLabels.closeGallery} />
          </Sheet.Close>
          <Button
            size="icon"
            className={classes.fullWidthGalleryPrev}
            onClick={() => setCurrentImagePosition("prev")}
          >
            <ChevronLeft label={STRINGS.gallery.ariaLabels.previousImage} />
          </Button>
          <List.Root snap={true} ref={galleryRefFunc}>
            {productImagesWithVideo.map((productImage, i) => {
              return (
                <List.Item
                  key={`productImage-${i}`}
                  ref={(el) => {
                    if (el) {
                      itemsRef.current[i] = el;
                    }
                  }}
                  tabIndex={i + 1}
                >
                  {productImage === "video" ? (
                    <VideoPlayer
                      src={videoUrls.desktop}
                      controls
                      autoPlay
                      muted
                      loop={false}
                      description={`${STRINGS.gallery.ariaLabels.videoDescription} ${productName}`}
                    />
                  ) : (
                    <Zoomable
                      panning={{ disabled: false, wheelPanning: true }}
                      wheel={{
                        disabled: false,
                        wheelDisabled: true,
                        step: 4.0,
                      }}
                      doubleClick={{ disabled: true }}
                      minScale={0.5}
                      maxScale={3}
                      centerOnInit={true}
                      limitToBounds={true}
                      alignmentAnimation={{ disabled: false }}
                    >
                      <CloudinaryImage
                        alt={`${STRINGS.gallery.ariaLabels.productImageAlt} ${i + 1}`}
                        loading="eager"
                        url={productImage}
                        srcSize={240}
                        sourceSetProfile={COLLAGE}
                        sizes="100vw"
                      />
                    </Zoomable>
                  )}
                </List.Item>
              );
            })}
          </List.Root>
          <Button
            size="icon"
            className={classes.fullWidthGalleryNext}
            onClick={() => setCurrentImagePosition("next")}
          >
            <ChevronRight label={STRINGS.gallery.ariaLabels.nextImage} />
          </Button>
        </Sheet.Main>
      </Sheet.Content>
    </Sheet.Root>
  );
};
